<script lang="ts" setup>
import { ref, watch, computed, defineProps, defineEmits } from 'vue';
import { i18n } from '@/i18n';

const locale = computed(() => i18n.locale);
const toLocaleStringOptions: Intl.NumberFormatOptions = {
  style: 'decimal',
  maximumFractionDigits: 7,
};

const inputRef = ref<HTMLInputElement | null>(null);
const isFocused = ref<boolean>(false);

const props = defineProps({
  modelValue: {
    type: Number,
    required: false,
  },
  placeholder: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  usePlaceholder: {
    type: Boolean,
    default: false,
  },
  autoResize: {
    type: Boolean,
    default: false,
  },
  suffix: {
    type: String,
    default: '',
  },
});
const emits = defineEmits(['update:modelValue', 'input']);

/**
 * inputのrefを設定する
 * @param ref - inputのref
 */
function setInputRef(ref: any) {
  inputRef.value = ref;
}

/**
 * フォーカス状態を設定する
 * @param focused - フォーカス状態
 */
const setFocused = (focused: boolean): void => {
  isFocused.value = focused;
  if (!inputRef.value) return;
  inputRef.value.type = focused ? 'number' : 'text';
  if (!focused) {
    emits('update:modelValue', parseNumber(inputRef.value.value));
  }
};

/**
 * 数値をフォーマットする
 * @param value - Formatする値
 */
function formatNumber(value: number | undefined): string {
  if (value === undefined) {
    return '';
  }
  if (isNaN(value)) {
    return '';
  }
  if (props.usePlaceholder) {
    return (value?.toLocaleString(locale.value, toLocaleStringOptions) ?? '') + props.suffix;
  }
  // TODO: i18n対応
  // https://developer.mozilla.org/ja/docs/Web/JavaScript/Reference/Global_Objects/Number/toLocaleString
  return (value?.toLocaleString(locale.value, toLocaleStringOptions) ?? '0') + props.suffix;
}

/**
 * パースした数値を返す
 * バリデーションも行っており、値のMax/Minをチェックし、小数点以下6桁に制限する
 * @param value - Parseする値
 */
function parseNumber(value: string): number {
  let num = parseFloat(value.replace(/[^\d.-]/g, ''));
  if (isNaN(num)) return 0;
  if (num > Number.MAX_SAFE_INTEGER) num = Number.MAX_SAFE_INTEGER;
  if (num < Number.MIN_SAFE_INTEGER) num = Number.MIN_SAFE_INTEGER;
  return parseFloat(num.toFixed(6)); // 小数点以下6桁に制限
}

/**
 * inputの内容に基づいて幅を調整する関数
 */
const adjustWidth = () => {
  if (props.modelValue === undefined) return;
  if (inputRef.value && props.autoResize) {
    // 値が10億未満の場合は、はみ出してしまうため、幅を調整する
    if (props.modelValue < 1000000) inputRef.value.style.width = `${formatNumber(props.modelValue).length + 3}ch`;
    else if (props.modelValue < 1000000000) inputRef.value.style.width = `${formatNumber(props.modelValue).length + 2}ch`;
    else if (props.modelValue < 1000000000000) inputRef.value.style.width = `${formatNumber(props.modelValue).length + 1}ch`;
    else inputRef.value.style.width = `${formatNumber(props.modelValue).length}ch`;
  }
};

// modelValueが変更された時にadjustWidthを呼び出す
watch(() => props.modelValue, adjustWidth);

// 初期描画時にadjustWidthを呼び出す
watch(inputRef, (newVal) => {
  if (newVal) {
    adjustWidth();
  }
});
</script>

<template>
  <input
    :ref="setInputRef"
    :value="isFocused ? modelValue : formatNumber(modelValue)"
    :placeholder="placeholder"
    :disabled="disabled"
    :max="Number.MAX_SAFE_INTEGER"
    :min="Number.MIN_SAFE_INTEGER"
    @focus="() => setFocused(true)"
    @blur="setFocused(false)"
    @input="
      (e: any) => {
        emits('input', parseNumber(e.target?.value ?? ''));
        adjustWidth();
      }
    "
  />
</template>
